/**
 * The Plugins catalogue: what you already have at the top, then the library
 * below it — featured first, then everything by category, with each long
 * category collapsed to a preview until you ask for the rest.
 *
 * Searching and the Connected filter share one flat result set. The selected
 * filter stays visible and applies to every search.
 *
 * Custom MCP servers are plugins too — the organization's and your own
 * personal ones: they sit with the accounts you have, match a search, and
 * count as connected while switched on.
 *
 * @module components/connections/PluginsTab
 */
import { Patch } from "@lucasmarkes/hairline/react";
import { useMemo, useState } from "react";

import type { ConnectionView, ConnectionsCapabilities } from "../../state/connections";
import type { CustomMcpServer } from "../../state/customMcpServers";
import { customMcpServerMatches } from "../apps/integrations/CustomMcpServerRow";
import { Button } from "../ui/button";
import { HairlineArt } from "../ui/hairline-art";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { CatalogueHeader } from "./CatalogueHeader";
import { CatalogueFilters } from "./CatalogueFilters";
import { CustomMcpPluginRow } from "./CustomMcpPluginRow";
import { PluginRow, type PluginRowActions } from "./PluginRow";
import { filterPlugins, partitionPluginCatalogue, type PluginSourceFilter } from "./pluginContent";

const SOURCE_FILTERS: ReadonlyArray<{ readonly id: PluginSourceFilter; readonly label: string }> = [
  { id: "all", label: "All" },
  { id: "connected", label: "Connected" },
];

const CATEGORY_PREVIEW_LIMIT = 6;

function SectionHeading({
  title,
  count,
}: {
  readonly title: string;
  readonly count?: number;
}) {
  return (
    <div className="border-b border-border/60 pb-2">
      <h2 className="text-base font-semibold tracking-tight text-foreground">
        {title}
        {count !== undefined ? (
          <span className="ml-2 text-sm font-normal text-muted-foreground">{count}</span>
        ) : null}
      </h2>
    </div>
  );
}

function PluginGrid({
  connections,
  capabilities,
  actions,
  customServers = [],
  onOpenCustomServer,
}: {
  readonly connections: ReadonlyArray<ConnectionView>;
  readonly capabilities: ConnectionsCapabilities;
  readonly actions: PluginRowActions;
  readonly customServers?: ReadonlyArray<CustomMcpServer>;
  readonly onOpenCustomServer?: ((serverId: string) => void) | undefined;
}) {
  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-1 pt-3 lg:grid-cols-2">
      {onOpenCustomServer
        ? customServers.map((server) => (
            <CustomMcpPluginRow key={server.id} server={server} onOpen={onOpenCustomServer} />
          ))
        : null}
      {connections.map((connection) => (
        <PluginRow
          key={connection.id}
          connection={connection}
          capabilities={capabilities}
          actions={actions}
        />
      ))}
    </div>
  );
}

function CategorySection({
  category,
  connections,
  capabilities,
  actions,
}: {
  readonly category: string;
  readonly connections: ReadonlyArray<ConnectionView>;
  readonly capabilities: ConnectionsCapabilities;
  readonly actions: PluginRowActions;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? connections : connections.slice(0, CATEGORY_PREVIEW_LIMIT);

  return (
    <section className="mt-6">
      <SectionHeading title={category} />
      <PluginGrid actions={actions} capabilities={capabilities} connections={visible} />
      {connections.length > CATEGORY_PREVIEW_LIMIT && !expanded ? (
        <Button
          variant="link"
          size="sm"
          className="mt-2 px-0 text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(true)}
        >
          See {connections.length - CATEGORY_PREVIEW_LIMIT} more
        </Button>
      ) : null}
    </section>
  );
}

export function PluginsTab({
  connections,
  capabilities,
  actions,
  searchQuery,
  isPending,
  available,
  customServers = [],
  onOpenCustomServer,
}: {
  readonly connections: ReadonlyArray<ConnectionView>;
  readonly capabilities: ConnectionsCapabilities;
  readonly actions: PluginRowActions;
  readonly searchQuery: string;
  readonly isPending: boolean;
  readonly available: boolean;
  /** The organization's and the viewer's personal custom MCP servers; opening one needs `onOpenCustomServer`. */
  readonly customServers?: ReadonlyArray<CustomMcpServer>;
  readonly onOpenCustomServer?: ((serverId: string) => void) | undefined;
}) {
  const [sourceFilter, setSourceFilter] = useState<PluginSourceFilter>("all");
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
  const custom = onOpenCustomServer ? customServers : [];
  const customFiltered = custom.filter(
    (server) =>
      customMcpServerMatches(server, normalizedQuery) &&
      (sourceFilter !== "connected" || server.enabled),
  );

  const {
    accounts: accountPlugins,
    featured,
    categories: byCategory,
  } = useMemo(() => partitionPluginCatalogue(connections), [connections]);

  const filtered = useMemo(
    () => filterPlugins(connections, sourceFilter, searchQuery),
    [connections, sourceFilter, searchQuery],
  );

  return (
    <div>
      <CatalogueHeader
        title="Plugins"
        description="Connect an account to use its tools and content in chat. You control access with Connect and Disconnect."
      />

      {available && !(isPending && connections.length === 0) ? (
        <div className="mt-6">
          <CatalogueFilters
            label="Filter plugins"
            options={SOURCE_FILTERS}
            value={sourceFilter}
            onChange={setSourceFilter}
          />
        </div>
      ) : null}

      {isPending && connections.length === 0 ? (
        <ListSkeleton className="mt-6" label="Loading plugins" />
      ) : !available ? (
        <p className="mt-6 text-sm text-muted-foreground">
          This server isn't configured to manage connections.
        </p>
      ) : normalizedQuery || sourceFilter === "connected" ? (
        <section className="mt-6" aria-live="polite">
          <SectionHeading
            title={normalizedQuery ? "Search results" : "Connected accounts"}
            count={filtered.length + customFiltered.length}
          />
          {filtered.length + customFiltered.length > 0 ? (
            <PluginGrid
              actions={actions}
              capabilities={capabilities}
              connections={filtered}
              customServers={customFiltered}
              onOpenCustomServer={onOpenCustomServer}
            />
          ) : normalizedQuery ? (
            <p className="pt-6 text-sm text-muted-foreground">
              {`No ${sourceFilter === "connected" ? "connected plugins" : "plugins"} match “${searchQuery.trim()}”. Try a name, account, category, or capability.`}
            </p>
          ) : (
            <div className="flex flex-col items-center pt-10 text-center">
              <HairlineArt figure={Patch} className="mb-4 w-48 sm:w-56" />
              <p className="text-sm text-muted-foreground">
                No accounts connected yet. Choose All to find a plugin to connect.
              </p>
            </div>
          )}
        </section>
      ) : (
        <>
          <section className="mt-6">
            <SectionHeading title="Your accounts" count={accountPlugins.length + custom.length} />
            {accountPlugins.length + custom.length > 0 ? (
              <PluginGrid
                actions={actions}
                capabilities={capabilities}
                connections={accountPlugins}
                customServers={custom}
                onOpenCustomServer={onOpenCustomServer}
              />
            ) : (
              <p className="pt-4 text-sm text-muted-foreground">
                No accounts connected yet. Choose a plugin below to connect your account.
              </p>
            )}
          </section>

          {featured.length > 0 ? (
            <section className="mt-6">
              <SectionHeading title="Featured" />
              <PluginGrid actions={actions} capabilities={capabilities} connections={featured} />
            </section>
          ) : null}

          {byCategory.map(([category, categoryConnections]) => (
            <CategorySection
              key={category}
              actions={actions}
              capabilities={capabilities}
              category={category}
              connections={categoryConnections}
            />
          ))}

          {filtered.length + custom.length === 0 ? (
            <p className="mt-10 text-sm text-muted-foreground">
              No plugins available in this view.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
